import Microgame from '../engine/Microgame.js';

export default class ToastGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Tartine !`, en: `Toast!`, es: `¡Tostada!` },
            controls: { fr: `Appuie sur le bouton du grille-pain au bon moment`, en: `Press the toaster button at the exact right moment`, es: `Pulsa el botón de la tostadora en el momento exacto` },
            objective: { fr: `Sors la tartine quand la jauge est dans le vert`, en: `Pop the toast out when the gauge is in the green zone`, es: `Saca la tostada cuando el medidor esté en la zona verde` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--secondary-color)" stroke-width="2"><path d="M4 14v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4"/><path d="M22 14c0-2.2-1.8-4-4-4H6c-2.2 0-4 1.8-4 4"/><path d="M6 10V6c0-1.1.9-2 2-2h8c1.1 0 2 .9 2 2v4"/><circle cx="12" cy="15" r="1"/></svg>`,
            actions: [
                { type: 'wait', duration: 520 },
                { type: 'tapTarget', selector: '[data-playback-target="toast-chip"]', duration: 170 },
                { type: 'wait', duration: 320 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.toaster = null;
        this.chip = null;
        this.state = 'waiting'; // waiting, popping, falling, done
        this.popTimer = null;
        this.velocity = 0;
        const speedMult = engine.speedMultiplier || 1.0;
        this.gravity = 1200 * (1 + (speedMult - 1) * 0.4);
        this.popForce = 900 * (1 + (speedMult - 1) * 0.3);
        this.chipY = 0;
        this.chipRotation = 0;
        this.rotationSpeed = 0;
    }

    setup() {
        this.engine.ui.instruction.textContent = "CATCH!";

        // Toaster body (secondary color)
        this.toaster = document.createElement('div');
        this.toaster.style.position = 'absolute';
        this.toaster.style.width = '180px';
        this.toaster.style.height = '140px';
        this.toaster.style.background = this.secondaryColor;
        this.toaster.style.bottom = '20%';
        this.toaster.style.left = '50%';
        this.toaster.style.transform = 'translateX(-50%)';
        this.toaster.style.borderRadius = '15px 15px 10px 10px';
        this.toaster.style.boxShadow = `0 0 20px ${this.secondaryColor}`;
        this.toaster.style.zIndex = '2';
        this.container.appendChild(this.toaster);

        // Slot
        const slot = document.createElement('div');
        slot.style.position = 'absolute';
        slot.style.width = '130px';
        slot.style.height = '8px';
        slot.style.background = '#000';
        slot.style.borderRadius = '4px';
        slot.style.top = '15px';
        slot.style.left = '50%';
        slot.style.transform = 'translateX(-50%)';
        this.toaster.appendChild(slot);

        // Lever (secondary, lighter)
        const lever = document.createElement('div');
        lever.style.position = 'absolute';
        lever.style.width = '12px';
        lever.style.height = '30px';
        lever.style.background = this.primaryColor;
        lever.style.borderRadius = '6px';
        lever.style.right = '-18px';
        lever.style.top = '60px';
        lever.style.boxShadow = `0 0 6px ${this.primaryColor}`;
        this.toaster.appendChild(lever);
        this.lever = lever;

        // Toast/Chip (primary color)
        this.chip = document.createElement('div');
        this.chip.style.position = 'absolute';
        this.chip.style.width = '110px';
        this.chip.style.height = '110px';
        this.chip.style.background = this.primaryColor;
        this.chip.style.borderRadius = '12px';
        this.chip.style.bottom = '20%';
        this.chip.style.left = '50%';
        this.chip.style.transform = 'translateX(-50%)';
        this.chip.style.zIndex = '1'; // Behind toaster initially
        this.chip.style.boxShadow = `0 0 10px ${this.primaryColor}40`;
        this.chip.dataset.playbackTarget = 'toast-chip';
        this.container.appendChild(this.chip);

        // Pop delay: scale with speed multiplier so it always fits in time
        const speedMult = this.engine.speedMultiplier || 1.0;
        const maxDelay = Math.max(200, 800 / speedMult);
        const delay = this.isPlayback ? 380 : 150 + Math.random() * (maxDelay - 150);
        this.popTimer = setTimeout(() => this.pop(), delay);

        this.onDown = this.onDown.bind(this);
        this.engine.input.element.addEventListener('pointerdown', this.onDown);
    }

    pop() {
        if (!this.active) return;
        this.state = 'popping';
        this.velocity = this.popForce;
        this.chipY = 0;
        // Random spin for fun
        this.rotationSpeed = (Math.random() - 0.5) * 400;

        // Lever clicks up
        this.lever.style.top = '30px';
        this.lever.style.transition = 'top 0.1s';

        // Toaster shake
        this.toaster.style.animation = 'none';
        this.toaster.offsetHeight; // force reflow
        this.toaster.style.animation = 'shake 0.15s ease-out';
    }

    update(dt) {
        if (!this.active || this.won || this.state === 'waiting') return;

        if (this.state === 'popping' || this.state === 'falling') {
            const dtSec = dt / 1000;

            // Apply gravity
            this.velocity -= this.gravity * dtSec;
            this.chipY += this.velocity * dtSec;

            // Spin
            this.chipRotation += this.rotationSpeed * dtSec;

            if (this.chipY <= 0) {
                this.chipY = 0;
                this.velocity = 0;
                this.rotationSpeed = 0;
                this.state = 'done'; // Landed back — missed it
            }

            // Update visual
            this.chip.style.bottom = `calc(20% + ${this.chipY}px)`;
            this.chip.style.transform = `translateX(-50%) rotate(${this.chipRotation}deg)`;

            // When above toaster, bring to front
            if (this.chipY > 50) {
                this.chip.style.zIndex = '3';
            }
        }
    }

    onDown(e) {
        if (!this.active || this.won) return;

        // Tapping before pop = lose (anti-spam)
        if (this.state === 'waiting') {
            this.lose();
            this.toaster.style.filter = 'brightness(0.5)';
            return;
        }

        // Check if chip is airborne and tapped
        const rect = this.chip.getBoundingClientRect();
        const hitMargin = 30; // Generous hit area

        if (
            e.clientX >= rect.left - hitMargin &&
            e.clientX <= rect.right + hitMargin &&
            e.clientY >= rect.top - hitMargin &&
            e.clientY <= rect.bottom + hitMargin
        ) {
            // Only win if actually popped up
            if (this.chipY > 15) {
                this.win();
                this.state = 'done';
                this.chip.style.boxShadow = `0 0 30px ${this.primaryColor}, 0 0 60px ${this.primaryColor}`;
                this.chip.style.transform = `translateX(-50%) rotate(${this.chipRotation}deg) scale(1.3)`;
                this.chip.style.zIndex = '5';
                this.chip.style.transition = 'transform 0.2s, box-shadow 0.2s';
            }
        }
    }

    cleanup() {
        super.cleanup();
        if (this.popTimer) clearTimeout(this.popTimer);
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
    }
}
